// Plik: frontend/src/pages/admin/ParticipantsList.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, TextField, Button, 
  IconButton, Dialog, DialogActions, DialogContent, 
  DialogContentText, DialogTitle, FormControlLabel, Checkbox,
  InputAdornment, CircularProgress, Alert, Chip, Grid,
  MenuItem, Select, FormControl, InputLabel, FormHelperText,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { adminService } from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';

const ParticipantsList = () => {
  const [participants, setParticipants] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  
  // Dialog edycji
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentParticipant, setCurrentParticipant] = useState(null);
  const [editFormData, setEditFormData] = useState({
    is_present: false,
    companions: 0,
    paid_participants: 0,
    slot_id: '',
    marketing_consent: false
  });
  
  // Pobierz uczestników
  const fetchParticipants = async () => {
    setLoading(true);
    setError(null);
    
    const filters = {};
    if (searchTerm) filters.search = searchTerm;
    if (selectedDate) filters.date = selectedDate;
    if (selectedSlot) filters.slotId = selectedSlot;
    
    const response = await adminService.getParticipants(filters);
    
    if (response.success) {
      setParticipants(response.data);
    } else {
      setError(response.error);
      toast.error('Nie udało się pobrać listy uczestników');
    }
    
    setLoading(false);
  };
  
  // Pobierz terminy
  const fetchSlots = async () => {
    const response = await adminService.getSlots();
    
    if (response.success) {
      setSlots(response.data);
    } else {
      toast.error('Nie udało się pobrać terminów');
    }
  };
  
  // Pobierz dane przy ładowaniu
  useEffect(() => {
    fetchParticipants();
    fetchSlots();
  }, []);
  
  // Załaduj dane ponownie po zmianie filtrów
  useEffect(() => {
    fetchParticipants();
  }, [searchTerm, selectedDate, selectedSlot]);
  
  // Obsługa wyszukiwania
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Obsługa filtrowania po dacie
  const handleDateFilter = (e) => {
    setSelectedDate(e.target.value);
    // Resetuj wybrany slot przy zmianie daty
    setSelectedSlot('');
  };
  
  // Obsługa filtrowania po slocie
  const handleSlotFilter = (e) => {
    setSelectedSlot(e.target.value);
  };
  
  // Otwórz dialog edycji
  const handleEditClick = (participant) => {
    setCurrentParticipant(participant);
    setEditFormData({
      is_present: participant.is_present === 1,
      companions: participant.companions || 0,
      paid_participants: participant.paid_participants || 0,
      slot_id: participant.slot_id,
      marketing_consent: participant.marketing_consent === 1
    });
    setEditDialogOpen(true);
  };
  
  // Otwórz dialog usuwania
  const handleDeleteClick = (participant) => {
    setCurrentParticipant(participant);
    setDeleteDialogOpen(true);
  };
  
  // Obsługa zmiany formularza edycji
  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Zapisz zmiany uczestnika
  const handleSaveEdit = async () => {
    setLoading(true);
    
    const response = await adminService.updateParticipant(
      currentParticipant.id,
      editFormData
    );
    
    if (response.success) {
      toast.success('Dane uczestnika zostały zaktualizowane');
      setEditDialogOpen(false);
      // Odśwież listę
      fetchParticipants();
    } else {
      toast.error(response.error || 'Nie udało się zaktualizować danych');
    }
    
    setLoading(false);
  };
  
  // Usuń uczestnika
  const handleDeleteConfirm = async () => {
    setLoading(true);
    
    const response = await adminService.deleteParticipant(currentParticipant.id);
    
    if (response.success) {
      toast.success('Uczestnik został usunięty');
      setDeleteDialogOpen(false);
      // Odśwież listę
      fetchParticipants();
    } else {
      toast.error(response.error || 'Nie udało się usunąć uczestnika');
    }
    
    setLoading(false);
  };
  
  // Pobierz unikalne daty z terminów
  const getUniqueDates = () => {
    const uniqueDates = [...new Set(slots.map(slot => slot.date))];
    return uniqueDates.sort();
  };
  
  // Pobierz sloty dla wybranej daty
  const getSlotsForDate = () => {
    if (!selectedDate) return [];
    return slots.filter(slot => slot.date === selectedDate);
  };
  
  // Eksportuj dane do CSV
  const handleExport = () => {
    adminService.exportData(selectedDate);
  };
  
  // Renderuj tabelę uczestników
  const renderParticipantsTable = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }
    
    if (participants.length === 0) {
      return (
        <Alert severity="info" sx={{ my: 2 }}>
          Brak uczestników spełniających kryteria wyszukiwania
        </Alert>
      );
    }
    
    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 650 }} aria-label="tabela uczestników">
          <TableHead>
            <TableRow>
              <TableCell>Imię i Nazwisko</TableCell>
              <TableCell>Szkoła</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Godzina</TableCell>
              <TableCell>Obecny</TableCell>
              <TableCell>Osoby Towarzyszące</TableCell>
              <TableCell>Zgody</TableCell>
              <TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {participants.map((participant) => (
              <TableRow key={participant.id}>
                <TableCell>{participant.full_name}</TableCell>
                <TableCell>{participant.school_name}</TableCell>
                <TableCell>{participant.email}</TableCell>
                <TableCell>{participant.phone}</TableCell>
                <TableCell>{participant.date}</TableCell>
                <TableCell>{participant.time}</TableCell>
                <TableCell>
                  {participant.is_present ? 
                    <Chip label="Tak" color="success" size="small" /> : 
                    <Chip label="Nie" color="default" size="small" />}
                </TableCell>
                <TableCell>{participant.companions || 0}</TableCell>
                <TableCell>
                  <Box>
                    <Tooltip title="Zgoda kontaktowa">
                      <Chip 
                        label="K" 
                        color={participant.contact_consent ? "primary" : "default"} 
                        size="small"
                        sx={{ mr: 0.5 }}
                      />
                    </Tooltip>
                    <Tooltip title="Zgoda marketingowa">
                      <Chip 
                        label="M" 
                        color={participant.marketing_consent ? "primary" : "default"} 
                        size="small" 
                      />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>
                  <IconButton 
                    aria-label="edytuj" 
                    onClick={() => handleEditClick(participant)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    aria-label="usuń" 
                    onClick={() => handleDeleteClick(participant)}
                    size="small"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <AdminLayout title="Lista Uczestników" maxWidth="xl">
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Lista Uczestników
      </Typography>
      
      {/* Filtry i wyszukiwanie */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Wyszukaj uczestnika"
              variant="outlined"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="date-filter-label">Filtruj po dacie</InputLabel>
              <Select
                labelId="date-filter-label"
                id="date-filter"
                value={selectedDate}
                label="Filtruj po dacie"
                onChange={handleDateFilter}
              >
                <MenuItem value="">
                  <em>Wszystkie daty</em>
                </MenuItem>
                {getUniqueDates().map(date => (
                  <MenuItem key={date} value={date}>
                    {new Date(date).toLocaleDateString('pl-PL', { 
                      weekday: 'long', day: 'numeric', month: 'long' 
                    })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth disabled={!selectedDate}>
              <InputLabel id="slot-filter-label">Filtruj po godzinie</InputLabel>
              <Select
                labelId="slot-filter-label"
                id="slot-filter"
                value={selectedSlot}
                label="Filtruj po godzinie"
                onChange={handleSlotFilter}
              >
                <MenuItem value="">
                  <em>Wszystkie godziny</em>
                </MenuItem>
                {getSlotsForDate().map(slot => (
                  <MenuItem key={slot.id} value={slot.id}>
                    {slot.time}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Button 
              variant="outlined" 
              fullWidth
              onClick={handleExport}
              disabled={loading}
            >
              Eksportuj do CSV
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Tabela uczestników */}
      {renderParticipantsTable()}
      
      {/* Dialog edycji uczestnika */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edycja uczestnika</DialogTitle>
        <DialogContent>
          {currentParticipant && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {currentParticipant.full_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {currentParticipant.email} | {currentParticipant.phone}
              </Typography>
              
              <Box sx={{ mt: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editFormData.is_present}
                      onChange={handleEditFormChange}
                      name="is_present"
                    />
                  }
                  label="Obecny"
                />
                
                <TextField
                  margin="dense"
                  label="Liczba osób towarzyszących"
                  type="number"
                  fullWidth
                  variant="outlined"
                  name="companions"
                  value={editFormData.companions}
                  onChange={handleEditFormChange}
                  InputProps={{ inputProps: { min: 0 } }}
                  sx={{ mt: 2 }}
                />
                
                <TextField
                  margin="dense"
                  label="Liczba płatnych uczestników"
                  type="number"
                  fullWidth
                  variant="outlined"
                  name="paid_participants"
                  value={editFormData.paid_participants}
                  onChange={handleEditFormChange}
                  InputProps={{ inputProps: { min: 0 } }}
                  sx={{ mt: 2 }}
                />
                
                {/* Dodanie pola zmiany zgody marketingowej */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editFormData.marketing_consent}
                      onChange={handleEditFormChange}
                      name="marketing_consent"
                    />
                  }
                  label="Zgoda marketingowa"
                  sx={{ mt: 2, display: 'block' }}
                />
                
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="slot-change-label">Zmień termin</InputLabel>
                  <Select
                    labelId="slot-change-label"
                    name="slot_id"
                    value={editFormData.slot_id}
                    label="Zmień termin"
                    onChange={handleEditFormChange}
                  >
                    {slots.map(slot => (
                      <MenuItem key={slot.id} value={slot.id} disabled={slot.available_spots <= 0 && slot.id !== currentParticipant.slot_id}>
                        {new Date(slot.date).toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' })} - {slot.time} 
                        {slot.available_spots <= 0 && slot.id !== currentParticipant.slot_id ? ' (brak miejsc)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Anuluj</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Zapisz zmiany'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog usuwania uczestnika */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Potwierdź usunięcie</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Czy na pewno chcesz usunąć uczestnika {currentParticipant?.full_name}?
            Ta operacja jest nieodwracalna.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Anuluj</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Usuń'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default ParticipantsList;